/**
 * NB-11 — note that generated text was cut because it cited a figure
 * the matter does not have.
 *
 * With two frozen figures, Generate everything wrote "Referring now to
 * FIG. 4 … a first chamber 410" and "Referring to FIG. 5, … a method
 * 500". The backend now removes such text before saving (see
 * backend/spec.py `drop_invented_figure_text`); this banner says what
 * was removed so the drafter knows the section was trimmed and why.
 * Advisory: what was kept is on the page.
 */
import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

export default function RemovedFigureBanner({
  sectionId,
}: {
  sectionId: string;
}): JSX.Element | null {
  const entry = useSpecStore((s) => s.figureWarnings[sectionId]);
  const dismiss = useSpecStore((s) => s.dismissFigureWarning);
  const section = useSpecStore(
    (s) => s.sections.find((sec) => sec.id === sectionId) || null,
  );

  if (!entry || entry.figures.length === 0) return null;
  if (section?.is_frozen) return null;

  const many = entry.figures.length > 1;
  return (
    <div
      className="mt-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-zinc-800"
      role="alert"
      data-testid={`spec-figure-warning-${sectionId}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
        <div className="flex-1 space-y-1">
          <div className="font-medium text-amber-900">
            {many ? 'Text about figures that do not exist was removed'
              : 'Text about a figure that does not exist was removed'}
          </div>
          <div className="text-zinc-700">
            {`The draft described ${entry.figures.join(', ')}, but ${
              many ? 'these are' : 'this is'} not among the frozen drawings, so that `
              + 'text was not saved. To describe another figure, add and freeze it in '
              + 'Drawings, then generate this section again.'}
          </div>
          {entry.excerpts.length > 0 && (
            <ul className="ml-2 list-disc pl-3 text-zinc-500" data-testid={`spec-figure-warning-list-${sectionId}`}>
              {entry.excerpts.map((x, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>{x.length >= 200 ? `${x}…` : x}</li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-400 hover:bg-amber-100 hover:text-zinc-700"
          onClick={() => dismiss(sectionId)}
          aria-label="Dismiss removed figure note"
          data-testid={`spec-figure-warning-dismiss-${sectionId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
